/**
 * ProductManagement component - handles product CRUD operations
 * This is a large component that manages the products tab in the admin panel
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { api, formatINR } from '../../lib/store';
import { formatSizeInput } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Image as ImageIcon, X, Wrench, Upload, Package, Zap, Download } from 'lucide-react';
import { logger } from '../../lib/logger';

import {
  CATEGORIES,
  RATE_TYPES,
  THICKNESS_BULK_CATEGORIES,
  WEIGHT_BULK_CATEGORIES,
  PLYWOOD_THICKNESSES,
  BLOCKBOARD_THICKNESSES,
  MDF_THICKNESSES,
  DOORS_THICKNESSES,
  WPCPVC_THICKNESSES,
  ACP_THICKNESSES,
  ADHESIVE_WEIGHTS,
  PLYWOOD_SIZES,
  DOOR_SIZES,
  DOOR_SIZES_WITH_SQFT,
  emptyProduct,
} from './constants';
import VeneerForm from './VeneerForm';

import { HardwareForm } from './HardwareForm';
import { HardwareSystemForm } from './HardwareSystemForm';
import { ProductFormDialog } from './productManagement/ProductFormDialog';
import { ImageUploadDialog } from './productManagement/ImageUploadDialog';
import { CategoryManageDialog } from './productManagement/CategoryManageDialog';
import { BulkUploadDialog } from './productManagement/BulkUploadDialog';

function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('Plywood'); // Default to Plywood
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null); // For grouped plywood editing
  const [form, setForm] = useState({ ...emptyProduct });
  const [saving, setSaving] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [imageProduct, setImageProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const imageInputRef = useRef(null);
  
  // Hardware form state (for Screws/Patti)
  const [showHardwareForm, setShowHardwareForm] = useState(false);
  const [editingHardware, setEditingHardware] = useState(null);
  
  // Hardware System form state (for Hardware category - tandems, hinges, etc.)
  const [showHardwareSystemForm, setShowHardwareSystemForm] = useState(false);
  const [editingHardwareSystem, setEditingHardwareSystem] = useState(null);
  
  // Veneer form state
  const [showVeneerForm, setShowVeneerForm] = useState(false);
  const [editingVeneer, setEditingVeneer] = useState(null);
  
  // Bulk upload state
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkUploadRef = useRef(null);
  
  // Custom categories state
  const [customCategories, setCustomCategories] = useState([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryPassword, setCategoryPassword] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(0); // 0: not started, 1: first confirm, 2: password
  
  // All available categories (default + custom)
  const allCategories = useMemo(() => {
    return [...CATEGORIES, ...customCategories].filter((v, i, a) => a.indexOf(v) === i);
  }, [customCategories]);
  
  // Load custom categories from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('hedaply_custom_categories');
    if (saved) {
      try {
        setCustomCategories(JSON.parse(saved));
      } catch (e) {
        logger.error('Failed to load custom categories');
      }
    }
  }, []);
  
  // Save custom categories to localStorage with password protection
  // Get stored passcode or use default (same as UserManagement)
  const getPasscode = () => {
    return localStorage.getItem('admin_passcode') || '9711';
  };
  
  const saveCustomCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }
    if (categoryPassword !== getPasscode()) {
      toast.error('Incorrect passcode');
      return;
    }
    const categoryName = newCategoryName.trim();
    if (allCategories.includes(categoryName)) {
      toast.error('Category already exists');
      return;
    }
    const updated = [...customCategories, categoryName];
    setCustomCategories(updated);
    localStorage.setItem('hedaply_custom_categories', JSON.stringify(updated));
    toast.success(`Category "${categoryName}" added`);
    setNewCategoryName('');
    setCategoryPassword('');
    setShowCategoryDialog(false);
  };
  
  // Delete custom category with two-step confirmation and password
  const deleteCustomCategory = (categoryName) => {
    if (deleteConfirmStep === 0) {
      setCategoryToDelete(categoryName);
      setDeleteConfirmStep(1);
      return;
    }
    if (deleteConfirmStep === 1) {
      setDeleteConfirmStep(2);
      return;
    }
    if (deleteConfirmStep === 2) {
      if (categoryPassword !== getPasscode()) {
        toast.error('Incorrect passcode');
        return;
      }
      const updated = customCategories.filter(c => c !== categoryName);
      setCustomCategories(updated);
      localStorage.setItem('hedaply_custom_categories', JSON.stringify(updated));
      toast.success(`Category "${categoryName}" deleted`);
      setCategoryToDelete(null);
      setDeleteConfirmStep(0);
      setCategoryPassword('');
    }
  };
  
  const cancelDelete = () => {
    setCategoryToDelete(null);
    setDeleteConfirmStep(0);
    setCategoryPassword('');
  };
  
  // For autocomplete suggestions
  const [allBrands, setAllBrands] = useState([]);
  const [allSubcategories, setAllSubcategories] = useState([]);
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [subcatSuggestions, setSubcatSuggestions] = useState([]);
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [showSubcatDropdown, setShowSubcatDropdown] = useState(false);
  
  // Plywood/Blockboard bulk add state
  const [plywoodRates, setPlywoodRates] = useState({});
  const [plywoodSizes, setPlywoodSizes] = useState([]); // Empty by default - user selects available sizes
  const [customThicknesses, setCustomThicknesses] = useState([]);
  const [newThickness, setNewThickness] = useState('');
  const [customSizes, setCustomSizes] = useState([]);
  const [newSize, setNewSize] = useState('');
  
  // Adhesives bulk add state (weights and volumes)
  const [adhesiveRates, setAdhesiveRates] = useState({});
  // Per-weight rate_type override (defaults auto-derived from the weight key)
  const [adhesiveRateTypes, setAdhesiveRateTypes] = useState({});
  // Optional 2nd rate per weight (e.g. argo tape can be sold per piece AND
  // per bundle). Key = weight id, value = { rate: string, rateType: string }.
  // When set, the cart shows rate-type chips and the user picks one.
  const [adhesiveSecondaryRates, setAdhesiveSecondaryRates] = useState({});

  // Best-guess unit from the pack label. User can still override via dropdown.
  const _deriveDefaultRateType = (weightLabel) => {
    const u = (weightLabel || '').toUpperCase();
    if (u.includes('LTR') || u.includes('LITRE') || u.includes('LITER')) return 'per liter';
    if (u.includes('ML')) return 'per ml';
    if (u.includes('GMS') || u.includes('GRAM')) return 'per gram';
    if (u.includes('KG')) return 'per kg';
    return 'per piece';
  };
  const rateTypeFor = (w) => adhesiveRateTypes[w] || _deriveDefaultRateType(w);
  const [customWeights, setCustomWeights] = useState([]);
  const [newWeight, setNewWeight] = useState('');
  
  // Check if category uses bulk thickness mode
  const isThicknessBulkCategory = THICKNESS_BULK_CATEGORIES.includes(form.main_category);
  
  // Check if category uses bulk weight mode (Adhesives)
  const isWeightBulkCategory = WEIGHT_BULK_CATEGORIES.includes(form.main_category);
  
  // Get appropriate thickness options based on category
  const getThicknessOptionsForCategory = (category) => {
    if (category === 'Blockboard') return BLOCKBOARD_THICKNESSES;
    if (category === 'Plywood') return PLYWOOD_THICKNESSES;
    if (category === 'MDF') return MDF_THICKNESSES;
    if (category === 'Doors') return DOORS_THICKNESSES;
    if (category === 'WPC/PVC') return WPCPVC_THICKNESSES;
    if (category === 'ACP') return ACP_THICKNESSES;
    return [];
  };
  
  // Helper to parse thickness value for sorting (handles decimals like 15.5MM)
  const parseThicknessValue = (thickness) => {
    const match = thickness.match(/^(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : 999;
  };
  
  // Helper to parse size and get square footage for sorting
  const parseSizeValue = (size) => {
    const match = size.match(/^(\d+\.?\d*)x(\d+\.?\d*)/i);
    if (match) {
      return parseFloat(match[1]) * parseFloat(match[2]);
    }
    return 0;
  };
  
  // Get all thicknesses (standard + custom) sorted in ascending order
  const getAllThicknessesSorted = (category) => {
    const standardThicknesses = getThicknessOptionsForCategory(category);
    const allThicknesses = [
      ...standardThicknesses,
      ...customThicknesses.map(t => ({ id: t, label: t }))
    ];
    // Sort by numeric value (ascending)
    return allThicknesses.sort((a, b) => parseThicknessValue(a.id) - parseThicknessValue(b.id));
  };
  
  // Helper to parse weight/volume value for sorting
  const parseWeightValue = (weight) => {
    const match = weight.match(/^(\d+\.?\d*)/);
    return match ? parseFloat(match[1]) : 999;
  };
  
  // Get all adhesive weights (standard + custom) sorted
  const getAllWeightsSorted = () => {
    // Combine standard weights (KG) and custom weights
    const allWeightsVolumes = [
      ...ADHESIVE_WEIGHTS,
      ...customWeights.map(w => ({ id: w, label: w }))
    ];
    // Sort by numeric value
    return allWeightsVolumes.sort((a, b) => parseWeightValue(a.id) - parseWeightValue(b.id));
  };
  
  // Add custom weight for adhesives
  const addCustomWeight = () => {
    if (!newWeight.trim()) return;
    let weightStr = newWeight.trim().toUpperCase();
    
    // Allow flexible formats: GMS, KG, LTR with optional space or special chars
    // Examples: 90GMS, 90 GMS, 90GMS F/S, 1.8KG, 2LTR
    // Just make sure it has some alphanumeric content
    if (weightStr.length < 2) {
      toast.error('Please enter a valid weight (e.g., 90GMS, 1.8KG, 2LTR)');
      return;
    }
    
    const allExisting = [...ADHESIVE_WEIGHTS.map(w => w.id), ...customWeights];
    if (allExisting.includes(weightStr)) {
      toast.error('This weight already exists');
      return;
    }
    setCustomWeights([...customWeights, weightStr]);
    setNewWeight('');
    toast.success(`Added ${weightStr}`);
  };
  
  // Get all sizes (standard + custom) sorted by area (descending - largest first)
  const getAllSizesSorted = () => {
    // Use door sizes when category is Doors
    const baseSizes = form.main_category === 'Doors' ? DOOR_SIZES_WITH_SQFT : PLYWOOD_SIZES;
    const allSizes = [
      ...baseSizes,
      ...customSizes
    ];
    // Sort by square footage (descending - largest first)
    return allSizes.sort((a, b) => b.sqft - a.sqft);
  };

  // Get default sizes for current category
  const getDefaultSizes = () => {
    if (form.main_category === 'Doors') {
      return DOOR_SIZES.slice(); // Use DOOR_SIZES constant
    }
    return ['10x4', '9x4', '8x4', '8x3', '7x4', '7x3', '6x4', '6x3'];
  };

  const fetchProducts = useCallback(() => {
    const params = { page, limit: 200 }; // Get more to group them
    if (search) params.search = search;
    if (filterCategory === 'QuickAdded') {
      params.quick_added = true;
    } else if (filterCategory === 'Orphans') {
      params.orphans = true;
    } else if (filterCategory && filterCategory !== 'All') {
      params.category = filterCategory;
    }
    api.getProducts(params).then(r => {
      setProducts(r.data.products);
      setTotal(r.data.total);
      setPages(r.data.pages);
    });
  }, [page, search, filterCategory]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);
  
  // Fetch all brands and subcategories for autocomplete
  useEffect(() => {
    api.getBrands().then(r => setAllBrands(r.data)).catch((err) => logger.warn('Failed to load brands:', err));
  }, []);
  
  // Update subcategory suggestions when category changes
  useEffect(() => {
    if (form.main_category) {
      api.getSubcategories(form.main_category).then(r => setAllSubcategories(r.data)).catch((err) => logger.warn('Failed to load subcategories:', err));
    } else {
      setAllSubcategories([]);
    }
  }, [form.main_category]);

  // Group products by brand + subcategory + grade for thickness/weight bulk categories
  // Also group Veneers by brand + name to show collections together
  const groupedProducts = useMemo(() => {
    // Special grouping for Veneers - group by brand + name
    if (filterCategory === 'Veneers') {
      const groups = {};
      products.forEach(p => {
        const key = `${p.brand_name}|||${p.subcategory || p.product_name}`;
        if (!groups[key]) {
          groups[key] = {
            key,
            brand_name: p.brand_name,
            subcategory: p.subcategory || p.product_name,
            product_name: p.product_name,
            main_category: p.main_category,
            veneer_type: p.veneer_type,
            code_prefix: p.code_prefix || '',  // Product-level code prefix
            status: p.status,
            image_url: p.image_url,
            // Per-product brand-in-PDF flag. We inherit from the FIRST variant
            // and treat the collection as a single editable unit — staff edit
            // the toggle once in the dialog and we fan it out to every code on save.
            show_brand_in_pdf: p.show_brand_in_pdf !== false,
            isGroup: true,
            isVeneerCollection: true,
            variants: [],
            codes: [], // Array of {code, rate_8x4, rate_10x4, stock_sheets, id}
          };
        }
        groups[key].variants.push(p);
        // Collect code info
        if (p.product_code) {
          groups[key].codes.push({
            code: p.product_code,
            rate_8x4: p.rate_8x4 || 0,
            rate_10x4: p.rate_10x4 || 0,
            stock_sheets: p.stock_sheets || 0,
            id: p.id,
          });
        }
        // Use first non-empty values
        if (!groups[key].image_url && p.image_url) groups[key].image_url = p.image_url;
        if (!groups[key].veneer_type && p.veneer_type) groups[key].veneer_type = p.veneer_type;
        if (!groups[key].code_prefix && p.code_prefix) groups[key].code_prefix = p.code_prefix;
      });
      return Object.values(groups);
    }
    
    if (!THICKNESS_BULK_CATEGORIES.includes(filterCategory) && !WEIGHT_BULK_CATEGORIES.includes(filterCategory)) {
      // For non-bulk categories, return products as-is (no grouping)
      return products.map(p => ({ ...p, isGroup: false, variants: [p] }));
    }
    
    // Group products by brand + subcategory + grade
    const groups = {};
    products.forEach(p => {
      const key = `${p.brand_name}|||${p.subcategory || p.product_name}|||${p.grade || ''}`;
      if (!groups[key]) {
        groups[key] = {
          key,
          brand_name: p.brand_name,
          subcategory: p.subcategory || p.product_name,
          product_name: p.product_name,
          main_category: p.main_category,
          grade: p.grade || '',
          product_code: p.product_code || '',
          brand_description: p.brand_description || '',  // Include brand_description
          rate_type: p.rate_type,
          status: p.status,
          image_url: p.image_url,
          available_sizes: p.available_sizes || [],
          // Per-product brand-in-PDF flag (inherit from first variant; staff
          // toggle it once and we fan-out the change to every thickness on save).
          show_brand_in_pdf: p.show_brand_in_pdf !== false,
          isGroup: true,
          variants: [],
          thicknessRates: {},
        };
      }
      groups[key].variants.push(p);
      groups[key].thicknessRates[p.thickness] = p.base_rate;
      // Use first non-empty values
      if (!groups[key].product_code && p.product_code) groups[key].product_code = p.product_code;
      if (!groups[key].brand_description && p.brand_description) groups[key].brand_description = p.brand_description;
      if (!groups[key].image_url && p.image_url) groups[key].image_url = p.image_url;
      if (p.available_sizes?.length > 0 && groups[key].available_sizes.length === 0) {
        groups[key].available_sizes = p.available_sizes;
      }
    });
    return Object.values(groups);
  }, [products, filterCategory]);

  const openAdd = () => { 
    setEditingProduct(null);
    setEditingGroup(null);
    setForm({ ...emptyProduct, main_category: filterCategory }); 
    setPlywoodRates({});
    setPlywoodSizes([]); // Empty by default - user selects available sizes
    setCustomThicknesses([]);
    setCustomSizes([]);
    setAdhesiveRates({});
    setAdhesiveRateTypes({});
    setAdhesiveSecondaryRates({});
    setCustomWeights([]);
    setShowDialog(true); 
  };

  const openEdit = (item) => {
    if (item.isGroup) {
      // Editing a grouped product - load ALL thickness/weight rates
      setEditingGroup(item);
      setEditingProduct(item.variants[0]); // Use first variant as base
      setForm({
        brand_name: item.brand_name || '',
        main_category: item.main_category || 'Plywood',
        subcategory: item.subcategory || '',
        product_name: item.product_name || '',
        product_code: item.product_code || '',
        brand_description: item.brand_description || '',  // Include brand_description from group
        thickness: '',
        size: '8x4',
        grade: item.grade || '',
        finish: '',
        rate_type: item.rate_type || 'per sq ft',
        base_rate: 0,
        gst_included: true,
        image_url: item.image_url || '',
        status: item.status || 'active',
        // Inherit brand-in-PDF flag from the group (defaults from first variant
        // via groupedProducts). Staff can flip it once and we fan-out to every
        // variant on save.
        show_brand_in_pdf: item.show_brand_in_pdf !== false,
      });
      
      // Check if this is an Adhesives group
      if (WEIGHT_BULK_CATEGORIES.includes(item.main_category)) {
        // Populate ALL weight rates from the group (thickness field stores weight for adhesives)
        const weightRates = {};
        const weightRateTypes = {};
        const secondaryRates = {};
        item.variants.forEach(v => {
          if (v.thickness && v.base_rate) {
            weightRates[v.thickness] = v.base_rate;
            if (v.rate_type) weightRateTypes[v.thickness] = v.rate_type;
            if (v.secondary_base_rate && parseFloat(v.secondary_base_rate) > 0) {
              secondaryRates[v.thickness] = {
                rate: String(v.secondary_base_rate),
                rateType: v.secondary_rate_type || 'per bundle',
              };
            }
          }
        });
        setAdhesiveRates(weightRates);
        setAdhesiveRateTypes(weightRateTypes);
        setAdhesiveSecondaryRates(secondaryRates);
        
        // Check for custom weights (anything not in ADHESIVE_WEIGHTS standard list)
        const standardWeights = ADHESIVE_WEIGHTS.map(w => w.id);
        const customW = Object.keys(weightRates).filter(w => !standardWeights.includes(w));
        setCustomWeights(customW);
      } else {
        // Populate ALL thickness rates from the group
        setPlywoodRates(item.thicknessRates || {});
        
        // Set available sizes - keep existing or empty if none set
        setPlywoodSizes(item.available_sizes || []);
        
        // Check for custom thicknesses based on category
        const standardThicknesses = getThicknessOptionsForCategory(item.main_category).map(t => t.id);
        const customT = Object.keys(item.thicknessRates).filter(t => !standardThicknesses.includes(t));
        setCustomThicknesses(customT);
        setCustomSizes([]);
      }
      setShowDialog(true);
    } else {
      // Non-grouped product (non-Plywood/Blockboard)
      const p = item;
      setEditingProduct(p);
      setEditingGroup(null);
      setForm({
        brand_name: p.brand_name || '', main_category: p.main_category || '', subcategory: p.subcategory || '',
        product_name: p.product_name || '', product_code: p.product_code || '', thickness: p.thickness || '',
        size: p.size || '', grade: p.grade || '', finish: p.finish || '', rate_type: p.rate_type || 'per sheet',
        base_rate: p.base_rate || 0, gst_included: p.gst_included ?? true, image_url: p.image_url || '', status: p.status || 'active',
        secondary_base_rate: p.secondary_base_rate || 0,
        secondary_rate_type: p.secondary_rate_type || '',
        sqft_per_box: p.sqft_per_box || 0,
        pieces_per_box: p.pieces_per_box || 0,
        codes_and_rates: p.codes_and_rates || [], available_sizes: p.available_sizes || [],
        brand_description: p.brand_description || '',
        show_brand_in_pdf: p.show_brand_in_pdf !== false,
      });
      setShowDialog(true);
    }
  };
  
  // Check if we're in bulk thickness mode (Plywood or Blockboard)
  const isPlywoodBulkMode = isThicknessBulkCategory;
  
  // Filter brand suggestions based on input
  const handleBrandInput = (value) => {
    setForm(f => ({ ...f, brand_name: value }));
    if (value.length > 0) {
      const filtered = allBrands.filter(b => b.toLowerCase().includes(value.toLowerCase()));
      setBrandSuggestions(filtered.slice(0, 8));
      setShowBrandDropdown(filtered.length > 0);
    } else {
      setBrandSuggestions(allBrands.slice(0, 8));
      setShowBrandDropdown(true);
    }
  };
  
  // Filter subcategory suggestions based on input
  const handleSubcatInput = (value) => {
    setForm(f => ({ ...f, subcategory: value }));
    if (value.length > 0) {
      const filtered = allSubcategories.filter(s => s.toLowerCase().includes(value.toLowerCase()));
      setSubcatSuggestions(filtered.slice(0, 8));
      setShowSubcatDropdown(filtered.length > 0);
    } else {
      setSubcatSuggestions(allSubcategories.slice(0, 8));
      setShowSubcatDropdown(true);
    }
  };
  
  const selectBrand = (brand) => {
    setForm(f => ({ ...f, brand_name: brand }));
    setShowBrandDropdown(false);
  };
  
  const selectSubcat = (subcat) => {
    setForm(f => ({ ...f, subcategory: subcat }));
    setShowSubcatDropdown(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !imageProduct) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.uploadProductImage(imageProduct.id, formData);
      toast.success('Image uploaded successfully');
      setShowImageDialog(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  // Download Excel template for bulk upload
  const handleDownloadTemplate = async (category) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/export-template?category=${encodeURIComponent(category)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('heda_token')}`
        }
      });
      if (!response.ok) throw new Error('Failed to download template');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${category.toLowerCase().replace(/\//g, '-')}_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${category} template`);
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // Handle bulk upload Excel file
  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }
    
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/products/bulk-upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('heda_token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to upload');
      }
      
      const result = await response.json();
      toast.success(`Upload complete! ${result.inserted} new, ${result.updated} updated`);
      setShowBulkUploadDialog(false);
      fetchProducts();
    } catch (err) {
      toast.error(err.message || 'Failed to upload file');
    } finally {
      setBulkUploading(false);
      if (bulkUploadRef.current) bulkUploadRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.brand_name || !form.main_category) { toast.error('Brand and Category are required'); return; }
    setSaving(true);
    try {
      if (editingGroup) {
        // Check if editing Adhesives group
        if (WEIGHT_BULK_CATEGORIES.includes(form.main_category)) {
          // Editing Adhesives group - update weight variants
          const allWeightsVolumes = [...ADHESIVE_WEIGHTS.map(w => w.id), ...customWeights];
          const ratedWeights = allWeightsVolumes.filter(w => adhesiveRates[w] && parseFloat(adhesiveRates[w]) > 0);
          
          if (ratedWeights.length === 0) {
            toast.error('Please enter at least one rate for a weight/volume');
            setSaving(false);
            return;
          }
          
          const existingWeights = editingGroup.variants.map(v => v.thickness);
          let updated = 0;
          let created = 0;
          
          for (const weightVolume of ratedWeights) {
            const rateType = rateTypeFor(weightVolume);
            const existingVariant = editingGroup.variants.find(v => v.thickness === weightVolume);
            
            if (existingVariant) {
              await api.updateProduct(existingVariant.id, {
                brand_name: form.brand_name,
                main_category: form.main_category,
                subcategory: form.subcategory,
                product_name: form.product_name,
                product_code: form.product_code,
                thickness: weightVolume,
                grade: form.grade,
                rate_type: rateType,
                base_rate: parseFloat(adhesiveRates[weightVolume]) || 0,
                secondary_base_rate: parseFloat(adhesiveSecondaryRates[weightVolume]?.rate) || 0,
                secondary_rate_type: adhesiveSecondaryRates[weightVolume]?.rateType || '',
                status: form.status,
                show_brand_in_pdf: form.show_brand_in_pdf !== false,
              });
              updated++;
            } else {
              await api.createProduct({
                brand_name: form.brand_name,
                main_category: form.main_category,
                subcategory: form.subcategory,
                product_name: form.product_name,
                product_code: form.product_code,
                thickness: weightVolume,
                grade: form.grade,
                rate_type: rateType,
                base_rate: parseFloat(adhesiveRates[weightVolume]) || 0,
                secondary_base_rate: parseFloat(adhesiveSecondaryRates[weightVolume]?.rate) || 0,
                secondary_rate_type: adhesiveSecondaryRates[weightVolume]?.rateType || '',
                gst_included: true,
                status: 'active',
                show_brand_in_pdf: form.show_brand_in_pdf !== false,
              });
              created++;
            }
          }
          
          // Delete variants that no longer have rates
          for (const variant of editingGroup.variants) {
            if (!ratedWeights.includes(variant.thickness)) {
              await api.deleteProduct(variant.id);
            }
          }
          
          toast.success(`Updated ${updated} adhesives${created > 0 ? `, created ${created} new` : ''}`);
        } else {
          // Editing a grouped Plywood/Blockboard product - update ALL variants
          const thicknessOptions = getThicknessOptionsForCategory(form.main_category);
          const allThicknesses = [...thicknessOptions.map(t => t.id), ...customThicknesses];
          const allSizes = [...plywoodSizes, ...customSizes.map(cs => cs.id)];
          const ratedThicknesses = allThicknesses.filter(t => plywoodRates[t] && parseFloat(plywoodRates[t]) > 0);
          
          if (ratedThicknesses.length === 0) {
            toast.error('Please enter at least one rate for a thickness');
            setSaving(false);
            return;
          }
          
          // Get existing thicknesses in the group
          const existingThicknesses = editingGroup.variants.map(v => v.thickness);
          
          let updated = 0;
          let created = 0;
          
          // Update existing variants and create new ones
          for (const thickness of ratedThicknesses) {
            const existingVariant = editingGroup.variants.find(v => v.thickness === thickness);
            
            if (existingVariant) {
              // Update existing variant
              await api.updateProduct(existingVariant.id, {
                brand_name: form.brand_name,
                main_category: form.main_category,
                subcategory: form.subcategory,
                product_name: form.product_name,
                product_code: form.product_code,
                brand_description: form.brand_description || '',  // Include brand description
                thickness: thickness,
                grade: form.grade,
                rate_type: 'per sq ft',
                base_rate: parseFloat(plywoodRates[thickness]) || 0,
                available_sizes: allSizes,
                status: form.status,
                show_brand_in_pdf: form.show_brand_in_pdf !== false,
              });
              updated++;
            } else {
              // Create new variant for this thickness
              await api.createProduct({
                brand_name: form.brand_name,
                main_category: form.main_category,
                subcategory: form.subcategory,
                product_name: form.product_name,
                product_code: form.product_code,
                brand_description: form.brand_description || '',  // Include brand description
                thickness: thickness,
                size: allSizes[0] || '',
                grade: form.grade,
                rate_type: 'per sq ft',
                base_rate: parseFloat(plywoodRates[thickness]) || 0,
                gst_included: true,
                status: 'active',
                available_sizes: allSizes,
                show_brand_in_pdf: form.show_brand_in_pdf !== false,
              });
              created++;
            }
          }
          
          // Delete variants that no longer have rates
          for (const variant of editingGroup.variants) {
            if (!ratedThicknesses.includes(variant.thickness)) {
              await api.deleteProduct(variant.id);
            }
          }
          
          toast.success(`Updated ${updated} products${created > 0 ? `, created ${created} new` : ''}`);
        }
      } else if (editingProduct && !isPlywoodBulkMode && !isWeightBulkCategory) {
        // Normal edit mode for non-plywood/blockboard
        await api.updateProduct(editingProduct.id, form);
        toast.success('Product updated');
      } else if (isPlywoodBulkMode) {
        // Plywood/Blockboard bulk add mode - create multiple products
        const thicknessOptions = getThicknessOptionsForCategory(form.main_category);
        const allThicknesses = [...thicknessOptions.map(t => t.id), ...customThicknesses];
        const allSizes = [...plywoodSizes, ...customSizes.map(cs => cs.id)];
        const selectedRateType = form.rate_type || 'per sq ft';
        const selectedSizeUnit = form.size_unit || 'feet';
        const productsToCreate = allThicknesses
          .filter(t => plywoodRates[t] && parseFloat(plywoodRates[t]) > 0)
          .map(thickness => ({
            brand_name: form.brand_name,
            main_category: form.main_category,
            subcategory: form.subcategory,
            product_name: form.product_name,
            product_code: form.product_code,
            brand_description: form.brand_description || '',  // Include brand description
            thickness: thickness,
            size: allSizes[0] || '',
            size_unit: selectedSizeUnit,
            grade: form.grade,
            finish: form.finish,
            rate_type: selectedRateType,
            base_rate: parseFloat(plywoodRates[thickness]) || 0,
            gst_included: true,
            status: 'active',
            available_sizes: allSizes,
            show_brand_in_pdf: form.show_brand_in_pdf !== false,
          }));
        
        if (productsToCreate.length === 0) {
          toast.error('Please enter at least one rate for a thickness');
          setSaving(false);
          return;
        }
        
        // Create all products
        let created = 0;
        for (const product of productsToCreate) {
          await api.createProduct(product);
          created++;
        }
        toast.success(`Created ${created} products for ${form.brand_name} ${form.subcategory}`);
      } else if (isWeightBulkCategory) {
        // Adhesives bulk add mode - create multiple products for each weight/volume
        const allWeightsVolumes = [...ADHESIVE_WEIGHTS.map(w => w.id), ...customWeights];
        const productsToCreate = allWeightsVolumes
          .filter(w => adhesiveRates[w] && parseFloat(adhesiveRates[w]) > 0)
          .map(weightVolume => ({
              brand_name: form.brand_name,
              main_category: form.main_category,
              subcategory: form.subcategory,
              product_name: form.product_name,
              product_code: form.product_code,
              thickness: weightVolume, // Use weight/volume as thickness for compatibility
              grade: form.grade,
              finish: form.finish,
              rate_type: rateTypeFor(weightVolume),
              base_rate: parseFloat(adhesiveRates[weightVolume]) || 0,
              secondary_base_rate: parseFloat(adhesiveSecondaryRates[weightVolume]?.rate) || 0,
              secondary_rate_type: adhesiveSecondaryRates[weightVolume]?.rateType || '',
              gst_included: true,
              status: 'active',
              show_brand_in_pdf: form.show_brand_in_pdf !== false,
          }));
        
        if (productsToCreate.length === 0) {
          toast.error('Please enter at least one rate for a weight/volume');
          setSaving(false);
          return;
        }
        
        // Create all products
        let created = 0;
        for (const product of productsToCreate) {
          await api.createProduct(product);
          created++;
        }
        toast.success(`Created ${created} adhesive products for ${form.brand_name} ${form.subcategory}`);
      } else {
        // Normal single product add
        await api.createProduct(form);
        toast.success('Product created');
      }
      setShowDialog(false);
      setEditingGroup(null);
      fetchProducts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  
  // Add custom thickness
  const addCustomThickness = () => {
    if (newThickness && !customThicknesses.includes(newThickness.toUpperCase())) {
      setCustomThicknesses([...customThicknesses, newThickness.toUpperCase()]);
      setNewThickness('');
    }
  };
  
  // Toggle size selection
  const toggleSize = (size) => {
    if (plywoodSizes.includes(size)) {
      setPlywoodSizes(plywoodSizes.filter(s => s !== size));
    } else {
      setPlywoodSizes([...plywoodSizes, size]);
    }
  };

  const handleDelete = async (item) => {
    if (item.isGroup) {
      // Delete all variants in the group
      if (!window.confirm(`Delete ALL ${item.variants.length} thickness variants of ${item.brand_name} ${item.subcategory} ${item.grade}?`)) return;
      try {
        for (const variant of item.variants) {
          await api.deleteProduct(variant.id);
        }
        toast.success(`Deleted ${item.variants.length} products`);
        fetchProducts();
      } catch { toast.error('Failed to delete'); }
    } else {
      if (!window.confirm(`Delete ${item.brand_name} ${item.product_name || item.subcategory}?`)) return;
      try { await api.deleteProduct(item.id); toast.success('Product deleted'); fetchProducts(); }
      catch { toast.error('Failed to delete'); }
    }
  };

  const handleToggleStatus = async (item) => {
    if (item.isGroup) {
      // Toggle status for all variants
      try {
        const newStatus = item.status === 'active' ? 'inactive' : 'active';
        for (const variant of item.variants) {
          await api.updateProduct(variant.id, { status: newStatus });
        }
        toast.success(`All variants ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchProducts();
      } catch { toast.error('Failed to toggle status'); }
    } else {
      try {
        const res = await api.toggleProductStatus(item.id);
        toast.success(`Product ${res.data.status === 'active' ? 'activated' : 'deactivated'}`);
        fetchProducts();
      } catch { toast.error('Failed to toggle status'); }
    }
  };

  const openImageUpload = (item) => {
    // For groups, use first variant
    const product = item.isGroup ? item.variants[0] : item;
    setImageProduct(product);
    setShowImageDialog(true);
  };

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      {/* Category Tabs — desktop: button row (visible at sm+). Mobile: a
          compact Select dropdown so the page doesn't get cluttered with
          12+ chips. (iter89 mobile pass.) */}
      <div className="mb-4 sm:hidden">
        <Select
          value={filterCategory}
          onValueChange={(v) => { setFilterCategory(v); setPage(1); }}
        >
          <SelectTrigger className="w-full" data-testid="admin-category-mobile-select">
            <SelectValue placeholder="Choose category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All products</SelectItem>
            {allCategories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
            <SelectItem value="QuickAdded">⚡ Quick Added</SelectItem>
            <SelectItem value="Orphans">🗑️ Orphans</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="hidden sm:flex flex-wrap gap-2 mb-4">
        {/* All — show every product (helps find orphan/mis-categorised ones) */}
        <Button
          variant={filterCategory === 'All' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setFilterCategory('All'); setPage(1); }}
          className={filterCategory === 'All' ? 'bg-[#1A1C16] text-white' : 'border-[#1A1C16] text-[#1A1C16]'}
          data-testid="all-products-filter-btn"
        >
          All
        </Button>
        {allCategories.map(cat => (
          <Button
            key={cat}
            variant={filterCategory === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFilterCategory(cat); setPage(1); }}
            className={filterCategory === cat ? 'bg-[#556B2F] text-white' : 'border-[#E2E6D9] text-[#5C6150]'}
          >
            {cat}
          </Button>
        ))}
        {/* Quick Added Products filter */}
        <Button
          variant={filterCategory === 'QuickAdded' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setFilterCategory('QuickAdded'); setPage(1); }}
          className={filterCategory === 'QuickAdded' ? 'bg-[#DAA520] text-white' : 'border-[#DAA520] text-[#8B6914]'}
          data-testid="quick-added-filter-btn"
        >
          <Zap className="w-3 h-3 mr-1" /> Quick Added
        </Button>
        {/* Orphans — products with non-standard main_category (mis-uploaded) */}
        <Button
          variant={filterCategory === 'Orphans' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setFilterCategory('Orphans'); setPage(1); }}
          className={filterCategory === 'Orphans' ? 'bg-[#B91C1C] text-white' : 'border-[#B91C1C] text-[#B91C1C]'}
          data-testid="orphans-filter-btn"
          title="Products with non-standard main_category (caused by bad bulk uploads). View and delete them."
        >
          <Trash2 className="w-3 h-3 mr-1" /> Orphans
        </Button>
        {filterCategory === 'Orphans' && products.length > 0 && (
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              if (!window.confirm(`Permanently delete ALL ${total} orphan products? This cannot be undone.`)) return;
              const pass = window.prompt('Enter passcode to confirm:');
              if (!pass) return;
              try {
                const r = await api.deleteOrphanProducts(pass);
                toast.success(`Deleted ${r.data.deleted_count} orphan products`);
                fetchProducts();
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to delete orphans');
              }
            }}
            className="bg-[#B91C1C] hover:bg-[#7F1D1D] text-white"
            data-testid="delete-all-orphans-btn"
          >
            <Trash2 className="w-3 h-3 mr-1" /> Delete All Orphans
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowCategoryDialog(true)}
          className="border-dashed border-[#DAA520] text-[#8B6914] hover:bg-[#DAA520]/10"
          data-testid="add-category-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Add Category
        </Button>
      </div>

      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search products..." className="pl-10 bg-white border-[#E2E6D9]" data-testid="admin-product-search" />
        </div>
        <div className="flex gap-2">
          {filterCategory === 'Screws/Patti' && (
            <Button 
              onClick={() => { setEditingHardware(null); setShowHardwareForm(true); }} 
              className="bg-[#DAA520] hover:bg-[#B8860B] text-white text-xs uppercase tracking-wider" 
              data-testid="admin-add-hardware-btn"
            >
              <Wrench className="w-4 h-4 mr-1" /> Add Screws/Patti
            </Button>
          )}
          {filterCategory === 'Veneers' && (
            <Button 
              onClick={() => { setEditingVeneer(null); setShowVeneerForm(true); }} 
              className="bg-[#8B4513] hover:bg-[#654321] text-white text-xs uppercase tracking-wider" 
              data-testid="admin-add-veneer-btn"
            >
              <Package className="w-4 h-4 mr-1" /> Add Veneer
            </Button>
          )}
          {filterCategory === 'Hardware' && (
            <Button 
              onClick={() => { setEditingHardwareSystem(null); setShowHardwareSystemForm(true); }} 
              className="bg-[#556B2F] hover:bg-[#465926] text-white text-xs uppercase tracking-wider" 
              data-testid="admin-add-hardware-system-btn"
            >
              <Package className="w-4 h-4 mr-1" /> Add Hardware
            </Button>
          )}
          {/* Show Add Product only for categories that don't have special forms */}
          {filterCategory !== 'Veneers' && filterCategory !== 'Hardware' && filterCategory !== 'Screws/Patti' && (
            <Button onClick={openAdd} className="bg-[#556B2F] hover:bg-[#465926] text-white text-xs uppercase tracking-wider" data-testid="admin-add-product-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Product
            </Button>
          )}
          
          {/* Bulk Upload Button */}
          <Button 
            onClick={() => setShowBulkUploadDialog(true)} 
            variant="outline" 
            className="text-xs uppercase tracking-wider border-[#556B2F] text-[#556B2F] hover:bg-[#F2F4EF]"
            data-testid="bulk-upload-btn"
          >
            <Upload className="w-4 h-4 mr-1" /> Bulk Upload
          </Button>
        </div>
      </div>

      {/* Product count */}
      <p className="text-sm text-muted-foreground mb-2">
        {groupedProducts.length} {(THICKNESS_BULK_CATEGORIES.includes(filterCategory) || WEIGHT_BULK_CATEGORIES.includes(filterCategory)) ? 'product groups' : filterCategory === 'Veneers' ? 'veneers' : 'products'} 
        {(THICKNESS_BULK_CATEGORIES.includes(filterCategory) || WEIGHT_BULK_CATEGORIES.includes(filterCategory)) && ` (${total} total variants)`}
      </p>

      <div className="bg-white border border-[#E2E6D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]" data-testid="admin-products-table">
          <thead>
            <tr className="bg-[#F2F4EF] border-b border-[#E2E6D9]">
              <th className="text-center py-2 px-2 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold w-10 sm:w-12">Img</th>
              <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Brand</th>
              <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">
                {filterCategory === 'Veneers' ? 'Collection' : 'Product'}
              </th>
              {filterCategory === 'Veneers' ? (
                <>
                  <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Species</th>
                  <th className="text-right py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold whitespace-nowrap">8x4</th>
                  <th className="text-right py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold whitespace-nowrap">10x4</th>
                  <th className="text-center py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Stock</th>
                </>
              ) : filterCategory === 'Screws/Patti' ? (
                <>
                  <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Type</th>
                  <th className="text-left py-2 px-2 sm:px-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground font-semibold">Variants</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Rate</th>
                </>
              ) : filterCategory === 'Hardware' ? (
                <>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Type</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Specs</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Variants</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Rate</th>
                </>
              ) : ['Laminates', 'Highlighters', 'Flooring'].includes(filterCategory) ? (
                <>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Code</th>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Finish</th>
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Rate</th>
                </>
              ) : (
                <>
                  <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Grade</th>
                  {THICKNESS_BULK_CATEGORIES.includes(filterCategory) ? (
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Thicknesses</th>
                  ) : WEIGHT_BULK_CATEGORIES.includes(filterCategory) ? (
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Weights / Volumes</th>
                  ) : (
                    <>
                      <th className="text-left py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Thickness</th>
                      <th className="text-right py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Rate</th>
                    </>
                  )}
                </>
              )}
              <th className="text-center py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Status</th>
              <th className="text-center py-2 px-3 text-xs uppercase tracking-wide text-muted-foreground font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {groupedProducts.map((item, idx) => (
              <tr key={item.isGroup ? item.key : item.id} className="border-b border-[#E2E6D9] hover:bg-[#F9FAF7]" data-testid={`admin-product-${idx}`}>
                <td className="py-2 px-2 text-center">
                  {item.image_url ? (
                    <img 
                      src={item.image_url.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : item.image_url} 
                      alt={item.brand_name} 
                      className="w-10 h-10 object-cover rounded border border-[#E2E6D9] cursor-pointer hover:opacity-80"
                      onClick={() => openImageUpload(item)}
                    />
                  ) : (
                    <button 
                      onClick={() => openImageUpload(item)} 
                      className="w-10 h-10 bg-[#F2F4EF] border border-dashed border-[#E2E6D9] rounded flex items-center justify-center hover:bg-[#E2E6D9] transition-colors"
                    >
                      <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                </td>
                <td className="py-2 px-3 font-semibold text-xs">{item.brand_name}</td>
                <td className="py-2 px-3 text-xs">
                  {filterCategory === 'Veneers' ? (
                    <div>
                      <span className="font-medium">{item.subcategory}</span>
                      {/* Show code prefix badge */}
                      {item.code_prefix && (
                        <span className="ml-2 px-1.5 py-0.5 bg-[#8B4513]/10 text-[#8B4513] text-[10px] font-mono rounded">
                          {item.code_prefix}
                        </span>
                      )}
                      {/* For veneer collections, show code count */}
                      {item.isVeneerCollection && item.codes?.length > 1 ? (
                        <span className="ml-2 text-[#556B2F] text-xs">({item.codes.length} codes)</span>
                      ) : item.product_code && (
                        <span className="ml-2 font-mono text-[#556B2F]">
                          {item.code_prefix ? `${item.code_prefix}-${item.product_code}` : item.product_code}
                        </span>
                      )}
                    </div>
                  ) : (
                    item.subcategory || item.product_name
                  )}
                </td>
                {filterCategory === 'Veneers' ? (
                  /* Veneer-specific columns - show collection info */
                  <>
                    <td className="py-2 px-3 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800">
                        {item.veneer_type || '-'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold text-[#556B2F]">
                      {/* For collections, show rate range */}
                      {item.isVeneerCollection && item.codes?.length > 0 ? (
                        (() => {
                          const rates = item.codes.filter(c => c.rate_8x4 > 0).map(c => c.rate_8x4);
                          if (rates.length === 0) return '-';
                          const min = Math.min(...rates);
                          const max = Math.max(...rates);
                          return min === max ? formatINR(min) : `${formatINR(min)}-${formatINR(max)}`;
                        })()
                      ) : item.rate_8x4 ? formatINR(item.rate_8x4) : '-'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold text-[#556B2F]">
                      {/* For collections, show rate range */}
                      {item.isVeneerCollection && item.codes?.length > 0 ? (
                        (() => {
                          const rates = item.codes.filter(c => c.rate_10x4 > 0).map(c => c.rate_10x4);
                          if (rates.length === 0) return '-';
                          const min = Math.min(...rates);
                          const max = Math.max(...rates);
                          return min === max ? formatINR(min) : `${formatINR(min)}-${formatINR(max)}`;
                        })()
                      ) : item.rate_10x4 ? formatINR(item.rate_10x4) : '-'}
                    </td>
                    <td className="py-2 px-3 text-center text-xs">
                      {/* For collections, show total stock across all codes */}
                      {item.isVeneerCollection && item.codes?.length > 0 ? (
                        (() => {
                          const totalStock = item.codes.reduce((sum, c) => sum + (c.stock_sheets || 0), 0);
                          const codesWithStock = item.codes.filter(c => c.stock_sheets > 0).length;
                          if (totalStock > 0) {
                            return (
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800 font-medium">
                                {totalStock} ({codesWithStock}/{item.codes.length})
                              </span>
                            );
                          }
                          return <span className="text-[#8F9485]">-</span>;
                        })()
                      ) : item.stock_sheets > 0 ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800 font-medium">
                          {item.stock_sheets} sheets
                        </span>
                      ) : (
                        <span className="text-[#8F9485]">-</span>
                      )}
                    </td>
                  </>
                ) : filterCategory === 'Screws/Patti' ? (
                  /* Screws/Patti specific columns */
                  <>
                    <td className="py-2 px-3 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 capitalize">
                        {item.hardware_type || '-'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {item.hardware_variants?.length > 0 ? (
                        <span className="text-[#556B2F] font-medium">{item.hardware_variants.length} size(s)</span>
                      ) : '-'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold text-[#556B2F]">
                      {(() => {
                        const variants = item.hardware_variants || [];
                        if (variants.length === 0) return '-';
                        // Get the first available rate from first variant
                        const v = variants[0];
                        if (v.rate_box) return `${formatINR(v.rate_box)}/box`;
                        if (v.rate_kg) return `${formatINR(v.rate_kg)}/kg`;
                        if (v.rate_running_feet) return `${formatINR(v.rate_running_feet)}/rft`;
                        if (v.rate_dozen) return `${formatINR(v.rate_dozen)}/dz`;
                        if (v.rate_piece) return `${formatINR(v.rate_piece)}/pc`;
                        return '-';
                      })()}
                    </td>
                  </>
                ) : filterCategory === 'Hardware' ? (
                  /* Hardware System specific columns */
                  <>
                    <td className="py-2 px-3 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-[#556B2F]/10 text-[#556B2F] capitalize">
                        {item.hardware_system_type?.replace('_', ' ') || item.subcategory || '-'}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-[#5C6150]">
                      {[item.load_capacity, item.feature, item.crank, item.color].filter(Boolean).join(' • ') || '-'}
                    </td>
                    <td className="py-2 px-3 text-xs">
                      {item.hardware_system_variants?.length > 0 ? (
                        <span className="text-[#556B2F] font-medium">{item.hardware_system_variants.length} size(s)</span>
                      ) : '-'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold text-[#556B2F]">
                      {item.hardware_system_variants?.length > 0 
                        ? `${formatINR(item.hardware_system_variants[0]?.rate || item.base_rate)}/${item.rate_type?.replace('per ', '') || 'set'}`
                        : formatINR(item.base_rate)
                      }
                    </td>
                  </>
                ) : ['Laminates', 'Highlighters', 'Flooring'].includes(filterCategory) ? (
                  <>
                    <td className="py-2 px-3 text-xs font-mono text-[#556B2F] font-semibold">{item.product_code || '-'}</td>
                    <td className="py-2 px-3 text-xs">{item.finish || '-'}</td>
                    <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold text-[#556B2F]">{formatINR(item.base_rate)}</td>
                  </>
                ) : (
                  <>
                    <td className="py-2 px-3 text-xs">{item.grade}</td>
                    {item.isGroup ? (
                      /* Plywood grouped view - show all thicknesses */
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap gap-1">
                          {item.variants.map((v, i) => (
                            <span key={i} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-[#556B2F]/10 text-[#556B2F]">
                              {v.thickness}: {formatINR(v.base_rate)}
                            </span>
                          ))}
                        </div>
                      </td>
                    ) : (
                      /* Non-grouped view */
                      <>
                        <td className="py-2 px-3 font-mono-data text-xs">{item.thickness}</td>
                        <td className="py-2 px-3 text-right font-mono-data text-xs font-semibold text-[#556B2F]">{formatINR(item.base_rate)}</td>
                      </>
                    )}
                  </>
                )}
                <td className="py-2 px-3 text-center">
                  <Badge variant={item.status === 'active' ? 'default' : 'secondary'}
                    className={`text-xs cursor-pointer ${item.status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-200' : 'bg-red-100 text-red-800 hover:bg-red-200'}`}
                    onClick={() => handleToggleStatus(item)}
                  >
                    {item.status === 'active' ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="py-2 px-3 text-center">
                  <div className="flex gap-1 justify-center">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#556B2F]" onClick={() => {
                      if (filterCategory === 'Veneers') {
                        setEditingVeneer(item);
                        setShowVeneerForm(true);
                      } else if (filterCategory === 'Screws/Patti') {
                        setEditingHardware(item);
                        setShowHardwareForm(true);
                      } else if (filterCategory === 'Hardware') {
                        setEditingHardwareSystem(item);
                        setShowHardwareSystemForm(true);
                      } else {
                        openEdit(item);
                      }
                    }}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#B93838]" onClick={() => handleDelete(item)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">Page {page} of {pages} ({total} products)</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Prop bundles — grouped for readability. Sub-components still
          destructure individual props (unchanged), so the runtime shape
          reaching each section is IDENTICAL to the previous 50-prop flat
          spread. Grouping just makes it obvious at a glance which pieces
          of state each mode of the dialog cares about, and makes it
          easier to spot when adding a new field which bundle it belongs
          to. */}
      {(() => {
        const formState = {
          form, setForm,
          editingGroup, setEditingGroup, editingProduct,
          showDialog, setShowDialog,
          saving, handleSave,
          isPlywoodBulkMode, isWeightBulkCategory,
          allCategories,
        };
        const brandAutocomplete = {
          brandSuggestions, showBrandDropdown, setShowBrandDropdown,
          handleBrandInput, selectBrand,
        };
        const subcatAutocomplete = {
          subcatSuggestions, showSubcatDropdown, setShowSubcatDropdown,
          handleSubcatInput, selectSubcat,
        };
        const plywoodBulk = {
          plywoodSizes, setPlywoodSizes,
          customSizes, setCustomSizes,
          plywoodRates, setPlywoodRates,
          customThicknesses, setCustomThicknesses,
          newThickness, setNewThickness, addCustomThickness,
          newSize, setNewSize, toggleSize,
          getAllThicknessesSorted, getAllSizesSorted,
        };
        const adhesivesBulk = {
          adhesiveRates, setAdhesiveRates,
          adhesiveSecondaryRates, setAdhesiveSecondaryRates,
          setAdhesiveRateTypes, rateTypeFor,
          customWeights, setCustomWeights,
          newWeight, setNewWeight, addCustomWeight,
        };
        return (
          <ProductFormDialog
            {...formState}
            {...brandAutocomplete}
            {...subcatAutocomplete}
            {...plywoodBulk}
            {...adhesivesBulk}
          />
        );
      })()}

      <ImageUploadDialog {...{ BACKEND_URL, handleImageUpload, imageInputRef, imageProduct, setShowImageDialog, showImageDialog, uploading }} />

      <CategoryManageDialog {...{ cancelDelete, categoryPassword, categoryToDelete, customCategories, deleteConfirmStep, deleteCustomCategory, newCategoryName, saveCustomCategory, setCategoryPassword, setCategoryToDelete, setDeleteConfirmStep, setNewCategoryName, setShowCategoryDialog, showCategoryDialog }} />

      {/* Hardware Form Dialog (for Screws/Patti) */}
      <HardwareForm
        open={showHardwareForm}
        onOpenChange={setShowHardwareForm}
        editProduct={editingHardware}
        onSaved={fetchProducts}
      />

      {/* Hardware System Form Dialog (for Hardware category - tandems, hinges, etc.) */}
      <HardwareSystemForm
        open={showHardwareSystemForm}
        onOpenChange={setShowHardwareSystemForm}
        editProduct={editingHardwareSystem}
        onSuccess={fetchProducts}
      />

      {/* Veneer Form Dialog */}
      <VeneerForm
        open={showVeneerForm}
        onOpenChange={setShowVeneerForm}
        editProduct={editingVeneer}
        onSuccess={fetchProducts}
      />

      <BulkUploadDialog {...{ bulkUploadRef, bulkUploading, handleBulkUpload, handleDownloadTemplate, products, setShowBulkUploadDialog, showBulkUploadDialog }} />
    </div>
  );
}


export { ProductManagement };
